"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";

// The query params this hook owns. Everything else in the URL is somebody
// else's (e.g. the candidate side sheet's ?candidate=) and gets carried through.
const OWNED_PARAMS = ["page", "sortBy", "sortDir", "q", "filters"];

function parseFilters(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Keeps a list page's page/sort/search/filters state mirrored into the URL
 * query string (via history.replaceState, no new history entries) so that
 * navigating to a detail page and back restores the exact same search
 * results instead of resetting to a blank list.
 *
 * Pair with `useUrlSync` (below), which owns the effect that pushes state
 * changes into the URL.
 */
export default function useUrlListState({
  defaultSortProperty,
  defaultSortDirection = "desc",
} = {}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Captured once on mount — this is what the page's state gets seeded from.
  // Later URL changes are driven by updateUrl(), not by re-reading this.
  const [initial] = useState(() => ({
    page: Number(searchParams.get("page")) || 1,
    sortBy: {
      property: searchParams.get("sortBy") || defaultSortProperty,
      direction: searchParams.get("sortDir") || defaultSortDirection,
    },
    search: searchParams.get("q") || "",
    filters: parseFilters(searchParams.get("filters")),
  }));

  // The URL we last wrote. `null` until the first call, which is the syncing
  // effect's mount run — that one would only write back the state we just read
  // above, so it's recorded without a replace(). Issuing a scroll:false
  // replace() there can swallow the browser's normal scroll-to-top when
  // navigating in from another page.
  const lastHrefRef = useRef(null);

  const updateUrl = useCallback(
    ({ page, sortBy, search, filters }) => {
      // Seeded from window.location rather than the useSearchParams() object
      // on purpose. Putting that object in this callback's deps would give
      // useUrlSync a new updateUrl identity on every URL change, firing a
      // replace() that overwrites the history entry a push() just created —
      // which would break back-button support for anything else in the URL.
      const params = new URLSearchParams(
        typeof window === "undefined" ? "" : window.location.search
      );
      OWNED_PARAMS.forEach((key) => params.delete(key));

      if (page && page !== 1) params.set("page", String(page));
      if (sortBy?.property && sortBy.property !== defaultSortProperty) {
        params.set("sortBy", sortBy.property);
      }
      if (sortBy?.direction && sortBy.direction !== defaultSortDirection) {
        params.set("sortDir", sortBy.direction);
      }
      if (search) params.set("q", search);
      if (filters && filters.length > 0) params.set("filters", JSON.stringify(filters));

      const qs = params.toString();
      const href = qs ? `${pathname}?${qs}` : pathname;

      // Nothing to write — this also absorbs re-renders where a state object's
      // identity changed but the values behind it didn't.
      if (lastHrefRef.current === href) return;

      const isMountRun = lastHrefRef.current === null;
      lastHrefRef.current = href;
      if (isMountRun) return;

      router.replace(href, { scroll: false });
    },
    [router, pathname, defaultSortProperty, defaultSortDirection]
  );

  return { initial, updateUrl };
}

/**
 * Mirrors a list page's live state into the URL.
 *
 * Split out from `useUrlListState` because that hook has to run *before* the
 * `useState` calls it seeds — which is exactly when the state it needs to
 * watch doesn't exist yet. Call this after the state (and `useSearch`) is
 * declared:
 *
 *   const { initial, updateUrl } = useUrlListState({ defaultSortProperty });
 *   const [page, setPage] = useState(initial.page);
 *   ...
 *   useUrlSync(updateUrl, { page, sortBy, search, filters: activeFilters });
 */
export function useUrlSync(updateUrl, { page, sortBy, search, filters }) {
  useEffect(() => {
    updateUrl({ page, sortBy, search, filters });
  }, [updateUrl, page, sortBy, search, filters]);
}
